/**
 * Group ordering for tables that hand row ordering to the API.
 *
 * A grouped table emits its groups in row order, and these tables pass `manualSorting`, so
 * the API's single sort field decides that order. Whenever the user groups by one column and
 * sorts by another, the grouped column's own order is lost — the groups come back in
 * whatever sequence their first row happened to appear in.
 *
 * A table fixes that by giving the grouped column a rank resolver: a function from a row to
 * that column's position in its own sequence (a stage's pipeline position, a status enum's
 * ordinal). Rows are stable-sorted by that rank before the table sees them, so the API's
 * ordering *within* each group survives untouched.
 */

/** A row's position in the grouped column's own sequence, or null when it has none. */
export type GroupRankResolver<T> = (row: T) => number | null | undefined;

/** Rank resolvers keyed by the table column id they order. */
export interface GroupRankResolvers<T> {
  [columnId: string]: GroupRankResolver<T>;
}

/**
 * Stable-sort `rows` by the grouped column's own order.
 *
 * Returns `rows` untouched when no resolver applies, so a table that groups by a column with
 * no inherent sequence (a name, an owner) keeps the API's ordering exactly as it arrived.
 */
export const orderRowsForGrouping = <T>(
  rows: T[],
  rankOf: GroupRankResolver<T> | undefined,
  desc: boolean = false
): T[] => {
  if (!rankOf) return rows;

  // Resolve every rank once: a resolver may do a map lookup, and the comparator runs O(n log n) times.
  const ranked = rows.map((row) => {
    const rank = rankOf(row);
    return { row, rank: rank == null ? null : rank };
  });

  const ordered = ranked.sort((firstEntry, secondEntry) => {
    if (firstEntry.rank === null && secondEntry.rank === null) return 0;
    // Rows with no place in the sequence group last in both directions, like the blank group.
    if (firstEntry.rank === null) return 1;
    if (secondEntry.rank === null) return -1;
    return desc
      ? secondEntry.rank - firstEntry.rank
      : firstEntry.rank - secondEntry.rank;
  });

  return ordered.map((entry) => entry.row);
};

/**
 * Rank a row by its own status ordinal — the same value the API orders that column on.
 *
 * Every status column groups on a value derived from the ordinal (sometimes the ordinal
 * itself, sometimes a label rendered from it), so ranking by it lands the groups in the
 * order sorting by that column would give. That is lifecycle order only where the enum
 * happens to declare it that way, which is not a given: `LeadStatus` declares its closed
 * states first (its open ones are the highest ordinals, per `LeadOpenStatuses`), so Leads
 * group terminal-first, and `ContractTableStatus` declares `ACTIVE` before `UPCOMING`,
 * reversing the order a contract reaches them.
 */
export const statusRank = <T extends { status?: number | null }>(
  row: T
): number | null => row.status ?? null;

/**
 * Resolve the rank resolver for the column a table is currently grouped by.
 *
 * Grouping is single-level through the UI, so only the first entry is consulted; a
 * multi-level `groupBy` from a hand-written URL falls through to no reordering rather than
 * reordering against the wrong level.
 */
export const resolveGroupRank = <T>(
  groupBy: string[] | undefined,
  resolvers: GroupRankResolvers<T> | undefined
): GroupRankResolver<T> | undefined => {
  if (!resolvers || !groupBy || groupBy.length !== 1) return undefined;
  return Object.prototype.hasOwnProperty.call(resolvers, groupBy[0])
    ? resolvers[groupBy[0]]
    : undefined;
};
